import React, { useState, useEffect } from 'react';
import formatNumber from '../../../helpers/NumberFormat';
import { FaRegFilePdf, FaFileCsv, FaFileInvoiceDollar } from "react-icons/fa";
import { IoReceiptSharp, IoSearchOutline } from "react-icons/io5";
import { FiRefreshCcw } from "react-icons/fi";
import { saveAs } from 'file-saver';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import moment from "moment";
import { PDFDownloadLink, Document, Page } from "@react-pdf/renderer";
import { toast } from "react-toastify";
import { useGetMainQuery } from "services/clientApi";
import Logo from '../../../assets/images/logo.png';
import "./BalanceAndMovements.scss"

// import MyDoc from "../../shared/PdfDoc/Pdf.jsx";
// import InvoicePdf from "../../shared/PdfDoc/InvoicePdf.jsx";
import ReceiptPdf from "../../shared/PdfDoc/ReceiptPdf.jsx";

export default function BalanceAndMovementsComponent({ isMobile, showSidebar }) {
    const {
        data: main,
        isLoading: mainLoading,
        isError: mainError,
        refetch,
    } = useGetMainQuery(undefined, {
        refetchOnMountOrArgChange: true,
    });
    const [refreshing, setRefreshing] = useState(false);
    const allTransactions = main?.user?.transactions || [];
    const [filtered, setFiltered] = useState([]);
    const [isFiltering, setIsFiltering] = useState(false);
    const transactionStatuses = main?.user?.statuses || [];

    useGetMainQuery(undefined, {
        refetchOnMountOrArgChange: true,
    });

    useEffect(() => {
        if (allTransactions.length > 0) {
            const sorted = [...allTransactions].sort(
                (a, b) => new Date(b.execution_date) - new Date(a.execution_date)
            );
            setFiltered(sorted.slice(0, 2));
        }
    }, [allTransactions]);

    const [filters, setFilters] = useState({
        period: '',
        minAmount: '',
        maxAmount: '',
        type: '',
        status: '',
    });

    const applyFilters = () => {
        let filteredData = [...allTransactions];

        if (filters.period !== 'all') {
            const days = parseInt(filters.period) || 30;
            const cutoff = new Date();
            cutoff.setDate(cutoff.getDate() - days);
            filteredData = filteredData.filter(tx => new Date(tx.execution_date) >= cutoff);
        }

        if (filters.minAmount)
            filteredData = filteredData.filter(tx => parseFloat(tx.amount) >= parseFloat(filters.minAmount));
        if (filters.maxAmount)
            filteredData = filteredData.filter(tx => parseFloat(tx.amount) <= parseFloat(filters.maxAmount));

        if (filters.type)
            filteredData = filteredData.filter(tx => tx.type === (filters.type === 'incoming' ? 'in' : 'out'));

        if (filters.status)
            filteredData = filteredData.filter(tx => tx.status?.name === filters.status);

        filteredData.sort((a, b) => new Date(b.execution_date) - new Date(a.execution_date));

        setFiltered(filteredData);
    };

    const handleRefresh = async () => {
        try {
            setRefreshing(true);
            await refetch();
        } finally {
            setRefreshing(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({ ...prev, [name]: value }));
    };

    const exportCSV = () => {
        const csvContent = [
            ['Date', 'Amount', 'Beneficiary', 'Type', 'Status', 'Reference'],
            ...filtered.map(tx => [
                tx.execution_date,
                tx.amount,
                tx.beneficary_name,
                tx.type,
                tx.status?.name,
                tx.reference_code,
            ])
        ].map(e => e.join(',')).join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        saveAs(blob, 'My-transactions.csv');
    };

    const exportPDF = () => {
        const doc = new jsPDF();

        const img = new Image();
        img.src = Logo;
        img.onload = () => {
            const pageWidth = doc.internal.pageSize.getWidth();
            const margin = 10;
            const logoWidth = 50;
            const logoHeight = 15;

            doc.setFontSize(16);
            doc.text(`Transaction Extract ${moment().format("DD/MM/YYYY")}`, margin, 20);

            const logoX = pageWidth - logoWidth - margin;
            const logoY = 10;
            doc.addImage(img, 'PNG', logoX, logoY, logoWidth, logoHeight);

            autoTable(doc, {
                startY: 30,
                head: [['Date', 'Amount', 'Beneficiary', 'Type', 'Status', 'Reference']],
                body: filtered.map(tx => [
                    moment(tx.execution_date).format("DD/MM/YYYY"),
                    formatNumber(tx.amount) + ' EUR',
                    tx.beneficary_name,
                    tx.type === 'out' ? 'Outgoing' : 'Incoming',
                    tx.status?.name ?? '',
                    tx.reference_code ?? '',
                ]),
            });

            doc.save('My-transactions.pdf');
        };
    };

    const handleSearch = async (e) => {
        e.preventDefault();
        setIsFiltering(true);
        setTimeout(() => {
            applyFilters();
            setIsFiltering(false);
        }, 750);
    };

    const handleClick = (filePath) => {
        if (!filePath) {
            toast.error("No receipt has been generated yet.");
            return;
        }

        window.open(baseUrl + filePath, "_blank");
    };

    return (
        <>
            <div
                className="container-fluid bg-col transition-width"
                style={
                    !isMobile
                        ? {
                            width: showSidebar ? 'calc(100% - 332px)' : '100%',
                            marginLeft: 0,
                            marginRight: 'auto',
                            transition: showSidebar ? 'width 0.3s ease-in-out' : 'width 0.16s ease-in-out'
                        } : {}
                }
            >
                <div className="row p-3 reports-panel">
                    <h5 className="mb-5 mt-2" style={{ fontWeight: "500" }}>BALANCE AND MOVEMENTS</h5>
                    <p className='text-muted'>Your account details are shown below. To view specific transactions, adjust the filters as needed and click the "Search" button.</p>

                    <div className="bg-custom-dark px-3 py-4 mb-3 rounded shadow-sm">
                        <h5 className="text-muted mb-4">Report Selection</h5>

                        <div className="row g-5">
                            <div className="col-md-6">
                                <div className="d-flex flex-column gap-2">
                                    <div className="d-flex justify-content-between">
                                        <span>Registered To:</span>
                                        <strong>{main?.user?.first_name} {main?.user?.last_name}</strong>
                                    </div>
                                    <div className="d-flex justify-content-between">
                                        <span>Account Number:</span>
                                        <strong>{main?.user?.detail?.account_number?.replace(/^.*?-/, '')}</strong>
                                    </div>
                                </div>
                            </div>

                            <div className="col-md-6">
                                <div className="d-flex flex-column gap-2">
                                    <div className="d-flex justify-content-between">
                                        <span>Available balance:</span>
                                        <strong>{formatNumber(main?.user?.balance)} €</strong>
                                    </div>
                                    <div className="d-flex justify-content-between">
                                        <span>Account balance:</span>
                                        <strong>{formatNumber(main?.user?.balance)} €</strong>
                                    </div>
                                    <div className="d-flex justify-content-between">
                                        <span>Credit limit:</span>
                                        <strong>{formatNumber(0)} €</strong>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <h5 className="text-muted mb-4 mt-5">Search parameters</h5>
                    <div className="row g-3 align-items-end mb-4">
                        <div className="col-md-6">
                            <label className="form-label">Period (accounting time)</label>
                            <select className="form-select" name="period" value={filters.period} onChange={handleInputChange}>
                                <option value="" disabled>Select period</option>
                                <option value="30">Last 30 days</option>
                                <option value="90">Last 90 days</option>
                                <option value="180">Last 180 days</option>
                                <option value="all">All time</option>
                            </select>
                        </div>

                        <div className="col-md-6">
                            <label className="form-label">Type</label>
                            <select className="form-select" name="type" value={filters.type} onChange={handleInputChange}>
                                <option value="">All</option>
                                <option value="incoming">Incoming</option>
                                <option value="outgoing">Outgoing</option>
                            </select>
                        </div>

                        <div className="col-md-4">
                            <label className="form-label">Status</label>
                            <select className="form-select" name="status" value={filters.status} onChange={handleInputChange}>
                                <option value="">All</option>
                                {transactionStatuses.map((status) => (
                                    <option value={status.name}>{status.name}</option>
                                ))}
                            </select>
                        </div>

                        <div className="col-md-3">
                            <label className="form-label">Amount From</label>
                            <input type="number" name="minAmount" value={filters.minAmount} onChange={handleInputChange} className="form-control" placeholder="Min €" />
                        </div>

                        <div className="col-md-3">
                            <label className="form-label">Amount To</label>
                            <input type="number" name="maxAmount" value={filters.maxAmount} onChange={handleInputChange} className="form-control" placeholder="Max €" />
                        </div>

                        <div className="col-md-2">
                            <button className="btn btn-primary w-100" onClick={handleSearch} disabled={isFiltering}>
                                {isFiltering ? (
                                    <div className="spinner-border spinner-border-sm text-light" role="status">
                                        <span className="visually-hidden">Loading...</span>
                                    </div>
                                ) : (
                                    <>
                                        <IoSearchOutline size={25} className="me-1" />
                                        Search
                                    </>
                                )}
                            </button>
                        </div>
                    </div>

                    {/* Filtered Transactions Table */}
                    <div className="card p-3 mt-5">
                        <div className="d-flex justify-content-start my-4 gap-5">
                            <div className='download-links' onClick={exportCSV}><FaFileCsv style={{ fontSize: '30px' }} /> Download CSV</div>
                            <div className='download-links' onClick={exportPDF}><FaRegFilePdf style={{ fontSize: '30px' }} /> Download PDF</div>
                        </div>

                        <div className="table-responsive">
                            <table className="table table-striped table-hover">
                                <thead className='table-head'>
                                    <tr>
                                        <th>Reference</th>
                                        <th>Date</th>
                                        <th>Beneficiary</th>
                                        <th className='text-end'>Amount (€)</th>
                                        <th className='text-center'>Type</th>
                                        <th className='text-center'>Status</th>
                                        {/* <th className='text-center'>Invoice</th> */}
                                        <th className='text-center'>Receipt</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filtered.map(tx => (
                                        <tr key={tx.id}>
                                            <td>{tx.reference_code}</td>
                                            <td>{moment(tx.execution_date).format("DD/MM/YYYY")}</td>
                                            <td>{tx.beneficary_name}</td>
                                            <td className='text-end'>{formatNumber(tx.amount)} €</td>
                                            <td className={tx.type === 'in' ? 'text-success text-center' : 'text-danger text-center'}>
                                                {tx.type === 'in' ? 'Incoming' : 'Outgoing'}
                                            </td>
                                            <td className='text-center'>
                                                <span style={{ color: tx.status?.color || '#666' }}>{tx.status?.name}</span>
                                            </td>
                                            {/* <td>
                                                <div
                                                    className="d-flex justify-content-center gap-2"
                                                    style={{ cursor: "pointer" }}
                                                >
                                                    <PDFDownloadLink
                                                        document={
                                                            // <MyDoc
                                                            //     details={tx}
                                                            //     account={main?.user?.detail?.account_number}
                                                            // />
                                                            <InvoicePdf
                                                                transaction={tx}
                                                                user={main?.user}
                                                            />

                                                        }
                                                        fileName={"Banque Artesia Nederland N.V. Invoice-" + tx.reference_code + 'T' + Date.now()}
                                                    >
                                                        {({ blob, url, loading, error }) =>
                                                            loading ? (
                                                                <div
                                                                    className="spinner-border spinner-border-sm text-primary"
                                                                    role="status"
                                                                />
                                                            ) : (
                                                                <FaFileInvoiceDollar
                                                                    className="info fa-solid fa-circle-info mt-1 mr-2 download-links"
                                                                    data-toggle="tooltip"
                                                                    data-placement="top"
                                                                    title="Download Your Invoice"
                                                                />
                                                            )
                                                        }
                                                    </PDFDownloadLink>
                                                </div>
                                            </td> */}
                                            <td>
                                                <div
                                                    className="d-flex justify-content-center gap-2"
                                                    style={{ cursor: "pointer" }}
                                                >
                                                    {tx.type === 'out' && tx.status?.name !== 'Declined' ? (
                                                        tx.receipt_file_path ? (
                                                            <div
                                                                className="d-flex justify-content-center gap-2"
                                                                style={{ cursor: "pointer" }}
                                                            >
                                                                <IoReceiptSharp
                                                                    className="download fa-solid fa-download mt-1 ml-2 download-links"
                                                                    data-toggle="tooltip"
                                                                    data-placement="top"
                                                                    title="Download Your Receipt"
                                                                    onClick={() => handleClick(tx.receipt_file_path)}
                                                                />
                                                            </div>
                                                        ) : (
                                                            <PDFDownloadLink
                                                                document={
                                                                    <ReceiptPdf
                                                                        transaction={tx}
                                                                        user={main?.user}
                                                                    />
                                                                }
                                                                fileName={"Banque Artesia Nederland N.V. Receipt-" + tx.reference_code + 'T' + Date.now()}
                                                            >
                                                                {({ blob, url, loading, error }) =>
                                                                    loading ? (
                                                                        <div
                                                                            className="spinner-border spinner-border-sm text-primary"
                                                                            role="status"
                                                                        />

                                                                    ) : (
                                                                        <IoReceiptSharp
                                                                            className="download fa-solid fa-download mt-1 ml-2 download-links"
                                                                            data-toggle="tooltip"
                                                                            data-placement="top"
                                                                            title="Download Your Receipt"
                                                                        />
                                                                    )
                                                                }
                                                            </PDFDownloadLink>
                                                        )
                                                    ) : (
                                                        <div
                                                            className="d-flex justify-content-center gap-2"
                                                            style={{ cursor: "pointer" }}
                                                        >
                                                            <IoReceiptSharp
                                                                className="download fa-solid fa-download mt-1 ml-2 download-links"
                                                                data-toggle="tooltip"
                                                                data-placement="top"
                                                                title="Download Your Receipt"
                                                                onClick={() => handleClick(tx.receipt_file_path)}
                                                            />
                                                        </div>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                    {filtered.length === 0 && (
                                        <tr>
                                            <td colSpan="7" className="text-center text-muted">No transactions match the selected filters.</td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>

                        <div className={`my-3 ${isMobile ? "text-center" : "text-end"}`}>
                            <button className="btn btn-primary col-md-2" onClick={handleRefresh} disabled={mainLoading || refreshing}>
                                {(mainLoading || refreshing) ? (
                                    <div className="spinner-border spinner-border-sm text-light" role="status">
                                        <span className="visually-hidden">Loading...</span>
                                    </div>
                                ) : (
                                    <>
                                        <FiRefreshCcw size={20} className="me-1" /> Refresh
                                    </>
                                )}
                            </button>
                        </div>
                    </div>

                    <div className='my-5'>
                        <h5>BUTTON LEGEND</h5>
                        <div className='text-muted'> *By default the table shows last two transactions. Filter ALL time to view all transactions.</div>
                        <div className='text-muted'><FaFileInvoiceDollar className='download-links' /> *Click this button at the transaction that you need to download the INVOICE in PDF format.</div>
                        <div className='text-muted'><IoReceiptSharp className='download-links' /> *Click this button at the transaction that you need to download the RECEIPT in PDF format.</div>
                        <div className='text-muted'><FiRefreshCcw className='download-links' /> *Click this button at the bottom of the transaction table to perform a manual refresh of the data.</div>
                    </div>
                </div>
            </div >
        </>
    );
}